import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  updateProfile: (data: { name: string; email: string }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  isAdmin: boolean;
  isModerator: boolean;
  isLawyer: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Sayfa yüklendiğinde kullanıcıyı localStorage'dan al
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Kullanıcı verisi parse edilemedi:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Kullanıcı değiştiğinde localStorage'ı güncelle
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  const login = async (email: string, password: string) => {
    const response = await authAPI.login(email, password);
    if (response.data.success && response.data.user && response.data.token) {
      setUser(response.data.user);
      localStorage.setItem('token', response.data.token);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout hatası:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  };

  const updateProfile = async (data: { name: string; email: string }) => {
    try {
      const response = await authAPI.updateProfile(data);
      if (response.data.success && response.data.user) {
        setUser(response.data.user);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Profil güncellenemedi');
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await authAPI.changePassword(currentPassword, newPassword);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Şifre değiştirilemedi');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Şifre değiştirilemedi');
    }
  };

  const deleteAccount = async () => {
    try {
      await authAPI.deleteAccount();
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Hesap silinemedi');
    }
  };

  const isAdmin = user?.role?.role_name === 'admin';
  const isLawyer = user?.role?.role_name === 'lawyer';
  const isModerator = isLawyer; // Backward compatibility

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        setUser,
        updateProfile,
        changePassword,
        deleteAccount,
        isAdmin,
        isModerator,
        isLawyer,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
